// src/ui/components/dailyReview.ts — Daily Review Gate Component (FR-SRS-002, FR-SRS-003)
// Presents <=5 spaced repetition review questions with a 3-5 minute visual timer cap,
// or a celebratory "All caught up" reef screen when no items are due.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import { SrsScheduler, type SrsItem } from '@storage/srsScheduler';
import { AdaptiveEngine } from '@storage/adaptiveEngine';
import { SessionGuard } from '@storage/sessionGuard';
import { DiscTimer } from '@ui/components/discTimer';
import { WaveTransitionCue } from '@ui/components/waveTransition';
import { interleaveQuestions, isSubTopicTransition } from '@stations/sprint/interleaver';
import { AppShell } from '@ui/shell';
import { StreakManager } from '@storage/streakManager';
import { StreakCelebration } from '@ui/components/streakCelebration';
import type { ClaweyHud } from '@ui/components/claweyHud';
import rawQuestions from '@content/questQuestions.json';
import type { QuestQuestion } from '../../stations/sprint/questEngine';

export interface DailyReviewOptions {
  hud?: ClaweyHud;
  durationSeconds?: number; // Default 300s (5 min, FR-SRS-003)
  onComplete?: () => void;
  now?: number;
}

export class DailyReviewComponent {
  private container: HTMLElement;
  private hud?: ClaweyHud;
  private durationSeconds: number;
  private onComplete?: () => void;
  private discTimer: DiscTimer | null = null;
  private dueItems: SrsItem[] = [];
  private reviewQuestions: QuestQuestion[] = [];
  private currentIndex: number = 0;
  private selectedOption: string | null = null;
  private hasAnsweredCurrent: boolean = false;
  private isCorrectCurrent: boolean = false;
  private correctCount: number = 0;
  private isSessionFinished: boolean = false;
  private sessionStartTime: number = 0;
  private timerExpired: boolean = false;

  constructor(options: DailyReviewOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'daily-review-container';
    this.container.className = 'daily-review-station';
    this.hud = options.hud;
    this.durationSeconds = options.durationSeconds ?? 300; // 5-minute cap
    this.onComplete = options.onComplete;
    this.sessionStartTime = Date.now();

    this.initializeQueue(options.now ?? Date.now());
  }

  private initializeQueue(now: number): void {
    this.dueItems = SrsScheduler.getDueItems(now, 5);
    const allBank = rawQuestions as QuestQuestion[];

    this.reviewQuestions = interleaveQuestions(
      this.dueItems.map((item) => {
        // Find matching question by id, topic or domain
        const match = allBank.find(
          (q) => q.id === item.id || q.topic.toLowerCase() === item.topic.toLowerCase()
        );
        if (match) return match;

        const domainMatch = allBank.find(
          (q) => q.domainName.toLowerCase().includes(item.domain.toLowerCase())
        );
        return domainMatch ?? allBank[0]!;
      })
    );

    // If no due items exist but we want to allow review or testing, questions will be empty
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    if (this.isSessionFinished) {
      this.renderSummary();
      return;
    }

    if (this.reviewQuestions.length === 0) {
      this.renderEmptyCelebration();
      return;
    }

    this.renderActiveReview();
  }

  /** Render empty state when all SRS reviews are up to date (FR-SRS-002) */
  private renderEmptyCelebration(): void {
    AppShell.setFocusMode(false);
    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="padding: 40px 32px; text-align: center; max-width: 680px; margin: 40px auto; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.12);">
        <div style="font-size: 3.5rem; margin-bottom: 16px;">🌊 🌟 🦞</div>
        <h2 style="font-family: ${tokens.font.heading}; font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 14px;">
          The Shallows Are Clear!
        </h2>
        <p style="font-size: 1.2rem; line-height: 1.6; color: ${tokens.color.morrisIndigo}; margin: 0 0 28px;">
          Splendid work, Tudor! All your memory clues are safely tucked into long-term recall. No topics are due for daily review right now.
        </p>

        <div style="display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          <a href="#/quest" id="btn-daily-to-quest" class="touch-target btn-primary" style="display: inline-flex; align-items: center; justify-content: center; padding: 14px 28px; font-size: 1.15rem; text-decoration: none;">
            🚀 Dive into Quest Sprints &rarr;
          </a>
          <a href="#/read" id="btn-daily-to-read" class="touch-target" style="display: inline-flex; align-items: center; justify-content: center; padding: 14px 24px; font-size: 1.1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; color: ${tokens.color.morrisIndigo}; font-weight: 800; text-decoration: none;">
            🔍 Clue Hunter
          </a>
        </div>
      </section>
    `;

    if (this.hud) {
      this.hud.say('All caught up on memory reviews, Tudor! You are in peak naturalist form today.', 'celebrating');
    }
  }

  /** Render active single-question SRS review screen */
  private renderActiveReview(): void {
    AppShell.setFocusMode(true);
    const q = this.reviewQuestions[this.currentIndex];
    if (!q) {
      this.finishSession();
      return;
    }

    const currentItem = this.dueItems[this.currentIndex];
    const questionNumber = this.currentIndex + 1;
    const totalQuestions = this.reviewQuestions.length;

    this.container.innerHTML = `
      <section style="max-width: 800px; margin: 0 auto; padding: 10px;">
        <!-- Top Session Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; padding: 16px 22px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; box-shadow: 0 4px 16px rgba(7, 20, 36, 0.08);">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              🌅 Spaced Memory Dive (5-Min Cap)
            </div>
            <h3 style="margin: 4px 0 0; font-size: 1.25rem; color: ${tokens.color.morrisIndigo}; font-family: ${tokens.font.heading};">
              Question ${questionNumber} of ${totalQuestions} · ${q.topic}
            </h3>
          </div>

          <div style="display: flex; align-items: center; gap: 12px;">
            ${currentItem ? `
              <span style="font-size: 0.85rem; padding: 4px 10px; border-radius: 12px; background: rgba(229, 169, 60, 0.15); color: ${tokens.color.morrisIndigo}; font-weight: 700;">
                ⚓ Interval: ${currentItem.intervalDays}d
              </span>
            ` : ''}

            <!-- Visual Disc Timer Mount -->
            <div id="daily-disc-mount" style="display: flex; align-items: center; justify-content: center; min-width: 48px; min-height: 48px;"></div>

            <button id="btn-daily-skip" class="touch-target focus-hidden-during-answering" style="padding: 6px 14px; font-size: 0.85rem; border: 1px solid rgba(26, 54, 93, 0.3); border-radius: 16px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer; font-weight: 700;">
              Finish Early
            </button>
          </div>
        </div>

        <!-- Single Question Card (FR-ADHD-004) -->
        <div class="morris-card" style="padding: 32px 28px; margin-bottom: 20px;">
          ${q.contextText ? `
            <div style="padding: 16px 20px; background: rgba(255, 253, 249, 0.96); border-left: 4px solid ${tokens.color.gildedBrass}; border-radius: 8px; margin-bottom: 20px; font-size: 1.15rem; line-height: 1.6; color: #1A365D;">
              ${q.contextText}
            </div>
          ` : ''}

          <h2 style="font-size: 1.35rem; line-height: 1.6; color: ${tokens.color.morrisIndigo}; margin: 0 0 24px;">
            ${q.prompt}
          </h2>

          <!-- Options Grid -->
          <div style="display: grid; grid-template-columns: 1fr; gap: 12px; margin-bottom: 26px;">
            ${q.options.map((opt: string) => {
              const isSelected = this.selectedOption === opt;
              let borderColor: string = tokens.color.gildedBrass;
              let bg = '#FFFDF9';
              let icon = '⚪';

              if (this.hasAnsweredCurrent) {
                if (opt === q.correctAnswer) {
                  borderColor = tokens.color.seaweedSage;
                  bg = 'rgba(61, 90, 69, 0.15)';
                  icon = '✅';
                } else if (isSelected) {
                  borderColor = tokens.color.madderCoral;
                  bg = 'rgba(255, 107, 74, 0.12)';
                  icon = '🔍';
                }
              } else if (isSelected) {
                borderColor = tokens.color.morrisIndigo;
                bg = 'rgba(26, 54, 93, 0.08)';
                icon = '🔵';
              }

              return `
                <button
                  class="touch-target btn-daily-option"
                  data-option="${opt}"
                  style="display: flex; align-items: center; gap: 12px; text-align: left; padding: 16px 20px; font-size: 1.1rem; border: 2px solid ${borderColor}; background: ${bg}; border-radius: 12px; cursor: pointer; transition: all 0.2s ease;"
                  ${this.hasAnsweredCurrent ? 'disabled' : ''}
                >
                  <span style="font-size: 1.25rem;">${icon}</span>
                  <span style="flex: 1; color: ${tokens.color.morrisIndigo}; font-weight: 600;">${opt}</span>
                </button>
              `;
            }).join('')}
          </div>

          <!-- Action Footer -->
          ${this.renderActionFooter(q, questionNumber, totalQuestions)}
        </div>
      </section>
    `;

    this.mountTimer();
    this.attachEventListeners();
  }

  private renderActionFooter(q: QuestQuestion, num: number, total: number): string {
    if (!this.hasAnsweredCurrent) {
      const canSubmit = this.selectedOption !== null;
      return `
        <button
          id="btn-daily-submit"
          class="touch-target btn-primary"
          style="padding: 14px 28px; font-size: 1.15rem; width: 100%; max-width: 340px;"
          ${canSubmit ? '' : 'disabled style="opacity: 0.5;"'}
        >
          Check Clue &rarr;
        </button>
      `;
    }

    const isLast = num >= total;

    return `
      <div style="padding: 16px 20px; background: rgba(250, 246, 237, 0.95); border-radius: 10px; border-left: 4px solid ${this.isCorrectCurrent ? tokens.color.seaweedSage : tokens.color.gildedBrass}; margin-bottom: 20px;">
        <div style="font-weight: 800; font-size: 1.05rem; color: ${this.isCorrectCurrent ? tokens.color.seaweedSage : tokens.color.morrisIndigo}; margin-bottom: 6px;">
          ${this.isCorrectCurrent ? '🌟 Memory Refreshed!' : '💡 Field Clue from Clawey:'}
        </div>
        <p style="margin: 0; font-size: 1.05rem; line-height: 1.5; color: ${tokens.color.morrisIndigo};">
          ${q.explanation}
        </p>
      </div>

      <button
        id="btn-daily-next"
        class="touch-target btn-primary"
        style="padding: 14px 28px; font-size: 1.15rem; width: 100%; max-width: 340px;"
      >
        ${!isLast ? 'Next Review Clue &rarr;' : 'Complete Memory Dive ⚓'}
      </button>
    `;
  }

  private mountTimer(): void {
    const mount = this.container.querySelector<HTMLElement>('#daily-disc-mount');
    if (!mount) return;

    if (!this.discTimer) {
      this.discTimer = new DiscTimer({
        durationSeconds: this.durationSeconds,
        size: 48,
        callbacks: {
          onExpire: () => {
            this.handleTimerExpiry();
          },
        },
      });
      this.discTimer.start();
    }

    mount.appendChild(this.discTimer.getElement());
  }

  private handleTimerExpiry(): void {
    this.timerExpired = true;
    soundEngine.playSuccess();
    if (this.hud) {
      this.hud.say('Five-minute brain dive finished, Tudor! Time to celebrate your focus.', 'celebrating');
    }
    this.finishSession();
  }

  private attachEventListeners(): void {
    // Option clicks
    const optionBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-daily-option');
    optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.hasAnsweredCurrent) return;
        soundEngine.playTap();
        this.selectedOption = btn.getAttribute('data-option');
        this.updateView();
      });
    });

    // Submit
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#btn-daily-submit');
    submitBtn?.addEventListener('click', () => {
      if (!this.selectedOption) return;
      this.processAnswer();
    });

    // Next
    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-daily-next');
    nextBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      if (this.currentIndex < this.reviewQuestions.length - 1) {
        const prevQ = this.reviewQuestions[this.currentIndex] ?? null;
        this.currentIndex += 1;
        const nextQ = this.reviewQuestions[this.currentIndex] ?? null;
        if (nextQ && isSubTopicTransition(prevQ, nextQ)) {
          const cue = new WaveTransitionCue({
            newSubTopic: nextQ.topic,
            domainName: nextQ.domainName,
          });
          cue.show(this.container);
        }
        this.selectedOption = null;
        this.hasAnsweredCurrent = false;
        this.isCorrectCurrent = false;
        this.updateView();
      } else {
        this.finishSession();
      }
    });

    // Finish Early
    const skipBtn = this.container.querySelector<HTMLButtonElement>('#btn-daily-skip');
    skipBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.finishSession();
    });
  }

  private processAnswer(): void {
    const q = this.reviewQuestions[this.currentIndex]!;
    const isCorrect = this.selectedOption === q.correctAnswer;
    const currentItem = this.dueItems[this.currentIndex];

    this.hasAnsweredCurrent = true;
    this.isCorrectCurrent = isCorrect;

    if (isCorrect) {
      this.correctCount += 1;
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say('Spot on, Tudor! That memory is firmly anchored now.', 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say('Good thinking, Tudor. Reviewing tricky concepts is how memory cements itself.', 'thinking');
      }
    }

    // Update SRS schedule
    if (currentItem) {
      SrsScheduler.recordReview({
        id: currentItem.id,
        topic: currentItem.topic,
        domain: currentItem.domain,
        correct: isCorrect,
      });
    }

    // Update Adaptive Engine Topic Mastery
    AdaptiveEngine.recordAttempt(q.topic, isCorrect);

    this.updateView();
  }

  private finishSession(): void {
    AppShell.setFocusMode(false);
    this.isSessionFinished = true;
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }

    const elapsedSeconds = Math.round((Date.now() - this.sessionStartTime) / 1000);
    SessionGuard.recordPracticeTime(elapsedSeconds);
    SrsScheduler.markSessionCompleted();

    // Record streak with 48h grace pause (FR-ADHD-005)
    const streakRes = StreakManager.recordActivity();
    if (streakRes.milestoneHit) {
      const celebration = new StreakCelebration({
        milestone: streakRes.milestoneHit,
      });
      document.body.appendChild(celebration.getElement());
    }

    this.updateView();

    if (this.onComplete) {
      this.onComplete();
    }
  }

  private renderSummary(): void {
    const totalReviewed = Math.min(this.currentIndex + (this.hasAnsweredCurrent ? 1 : 0), this.reviewQuestions.length);

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="padding: 40px 32px; text-align: center; max-width: 680px; margin: 30px auto; box-shadow: 0 8px 32px rgba(10, 25, 47, 0.12);">
        <div style="font-size: 3.5rem; margin-bottom: 16px;">🌊 ⚓ 🌟</div>
        <h2 style="font-family: ${tokens.font.heading}; font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 10px;">
          Daily Memory Dive Complete!
        </h2>
        <p style="font-size: 1.15rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 24px;">
          ${this.timerExpired
            ? 'Time cap reached! You stayed fully focused for 5 minutes.'
            : 'All queued review questions completed! Your long-term memory roots are deepening.'}
        </p>

        <!-- Stats Grid -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 14px; margin-bottom: 30px;">
          <div style="padding: 16px; background: rgba(255, 253, 249, 0.95); border-radius: 12px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <span style="display: block; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800;">REVIEWED</span>
            <span style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${totalReviewed}</span>
          </div>
          <div style="padding: 16px; background: rgba(255, 253, 249, 0.95); border-radius: 12px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <span style="display: block; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800;">ACCURACY</span>
            <span style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.seaweedSage};">
              ${totalReviewed > 0 ? Math.round((this.correctCount / totalReviewed) * 100) : 0}%
            </span>
          </div>
          <div style="padding: 16px; background: rgba(255, 253, 249, 0.95); border-radius: 12px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <span style="display: block; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800;">DAILY GATE</span>
            <span style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">✓ Clear</span>
          </div>
        </div>

        <div style="display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          <a href="#/quest" id="btn-summary-to-sprint" class="touch-target btn-primary" style="display: inline-flex; align-items: center; justify-content: center; padding: 14px 28px; font-size: 1.15rem; text-decoration: none;">
            🚀 Continue to Quest Sprints &rarr;
          </a>
          <a href="#/read" id="btn-summary-to-read" class="touch-target" style="display: inline-flex; align-items: center; justify-content: center; padding: 14px 24px; font-size: 1.1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 12px; color: ${tokens.color.morrisIndigo}; font-weight: 800; text-decoration: none;">
            📖 English Stations
          </a>
        </div>
      </section>
    `;
  }

  destroy(): void {
    if (this.discTimer) {
      this.discTimer.destroy();
      this.discTimer = null;
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
